// This file should never have any imports

import type { BaseBuilderVersion, Component, Flow, FlowPage } from '../models/types'

const SEPARATOR = '-'

export function generateComponentClasses({
  component,
  page,
  builderVersion,
}: {
  component: Component
  page?: FlowPage
  builderVersion: BaseBuilderVersion
}): string[] {
  const classes = page
    ? [
        'component',
        `cid-${component.id}`,
        `in-page-key-${page.key}`,
        ...(page.tags || []).map((tag) => `in-page-tag-${tag}`),
      ]
    : []

  if (component.mask_recording) classes.push('savvy-disable')
  if (component.ignore_recording) classes.push('savvy-disable-input')
  if (component.tags)
    classes.push(
      ...component.tags.map((tag) => `component-tag-${tag}`),
      ...component.tags.map((tag) => `tag-${tag}`)
    )

  classes.push(...generateComponentSelectors({ component, page, builderVersion }))

  classes.push(...[`component-key-${component.key}`])

  return Array.from(new Set(classes))
}

export function generateElementSelectors({
  elementType,
  component = null,
  page,
  elementTags,
  elementExtras,
  builderVersion,
}: {
  elementType: string
  component?: Component | null
  page: FlowPage
  elementTags?: string[]
  elementExtras?: { [key: string]: string }
  builderVersion: BaseBuilderVersion
}): string[] {
  const classes = [
    'Flow-Element',
    elementType ? `ElementType-${elementType}` : null,
    ...(elementTags || []).map((tag) => `ElementTag-${tag}`),
    ...Object.entries(elementExtras || {}).map(([k, v]) => `Element${k}-${v}`),
  ]
    .concat(
      (component
        ? generateComponentSelectors({ component, page, builderVersion })
        : generatePageSelectors(page)
      ).slice(1)
    )
    .filter((s) => s) as string[]
  return Array.from(new Set(classes))
}

export function generateComponentSelectors({
  component,
  page,
  builderVersion,
}: {
  component: Component
  page?: FlowPage
  builderVersion: BaseBuilderVersion
}): string[] {
  const bases = {
    ComponentType: [component.type],
    ComponentTag: component.tags || [],
    PageTag: (page && page.tags) || [],
    PageKey: page ? [page.key] : [],
    ComponentKey: [component.key],
  }

  //* In builder v4, the parent_key is deprecated and only the parent_id is used
  const parentRef =
    builderVersion > 3 ? component.parent_id : component.parent_key || component.parent_id
  const isInContainer = parentRef

  const classes = Object.entries(bases).reduce((acc, [key, values]) => {
    values.forEach((v) => {
      const selector = `${key}${SEPARATOR}${v}`
      acc.add(selector)
      if (isInContainer) {
        if (builderVersion > 3) acc.add(`in-container-id-${isInContainer}`)
        else acc.add(`in-container-${component.parent_key ? 'key' : 'id'}-${isInContainer}`)
        acc.add(`InContainer${SEPARATOR}${isInContainer}`)
      }
    })
    return acc
  }, new Set(['Flow-Component']))
  return Array.from(classes)
}

export function generatePageSelectors(page: FlowPage): string[] {
  if (!page) return []
  const bases = { PageKey: [page.key], PageTag: page.tags || [] }

  const classes = Object.entries(bases).reduce(
    (acc, [key, values]) => {
      values.forEach((v) => acc.push(`${key}${SEPARATOR}${v}`))
      return acc
    },
    ['Flow-Page']
  )
  return Array.from(new Set(classes))
}
